import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ConfigProvider, App as AntApp, theme as antTheme } from 'antd'

/**
 * Colour mode. "system" follows the device; the explicit modes win and are
 * remembered. The resolved mode is stamped on <html data-theme> so styles.css
 * can switch tokens, and is mirrored into Ant Design's algorithm so its
 * controls match the rest of the page.
 */
export type Mode = 'light' | 'dark' | 'system'

type Ctx = { mode: Mode; resolved: 'light' | 'dark'; setMode: (m: Mode) => void }
const ThemeContext = createContext<Ctx>({ mode: 'system', resolved: 'light', setMode: () => {} })
export const useTheme = () => useContext(ThemeContext)

const KEY = 'argo.theme'

function stored(): Mode {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system'
  } catch { return 'system' }
}

const systemDark = () => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<Mode>(stored)
  const [systemIsDark, setSystemIsDark] = useState(systemDark)

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (!mq) return
    const onChange = (e: MediaQueryListEvent) => setSystemIsDark(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const resolved: 'light' | 'dark' = mode === 'system' ? (systemIsDark ? 'dark' : 'light') : mode

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolved)
  }, [resolved])

  const setMode = useCallback((m: Mode) => {
    setModeState(m)
    try { localStorage.setItem(KEY, m) } catch { /* private mode */ }
  }, [])

  const value = useMemo(() => ({ mode, resolved, setMode }), [mode, resolved, setMode])
  const dark = resolved === 'dark'

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider
        theme={{
          algorithm: dark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
          token: {
            colorPrimary: dark ? '#2dd4bf' : '#0d9488',
            colorSuccess: dark ? '#26c281' : '#0e9f6e',
            colorError: dark ? '#f2495c' : '#dc3545',
            colorBgBase: dark ? '#0a0e14' : '#f6f8fb',
            colorBgContainer: dark ? '#131922' : '#ffffff',
            colorBgElevated: dark ? '#1a212c' : '#ffffff',
            colorBorder: dark ? '#232c39' : '#dde4ee',
            colorBorderSecondary: dark ? '#1a212c' : '#eef2f7',
            colorText: dark ? '#e9eef6' : '#0d1526',
            colorTextSecondary: dark ? '#a8b3c4' : '#475569',
            borderRadius: 10,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Noto Sans', sans-serif",
            fontSize: 14,
          },
          components: {
            Table: { headerBg: dark ? '#1a212c' : '#eff3f8' },
            Segmented: { itemSelectedBg: dark ? '#232c39' : '#ffffff' },
          },
        }}
      >
        <AntApp>{children}</AntApp>
      </ConfigProvider>
    </ThemeContext.Provider>
  )
}
